import React, { useEffect, useState, useCallback } from "react";
import { Table, Spinner, Button, Form, Row, Col, Badge, Alert as BsAlert, Card, Nav, Tab } from "react-bootstrap";
import SafetySopralluoghiService from "../../../services/safety/SafetySopralluoghiService";

const EMPTY_SOP = {
    tipo: "INTERNO",
    data_sopralluogo: "",
    luogo: "",
    oggetto: "",
    ispettore: "",
    esito_sintesi: "",
    note: "",
};

const EMPTY_NC = {
    id_sopralluogo: "",
    codice: "",
    gravita: "MINORE",
    descrizione: "",
    riferimento_normativo: "",
    area_rilevazione: "",
    responsabile: "",
    data_rilevo: "",
    data_scadenza_azione: "",
    stato_azione: "APERTA",
    azione_correttiva: "",
    note: "",
};

const TIPI_SOP = [
    { value: "INTERNO", label: "Audit interno" },
    { value: "ESTERNO", label: "Consulente esterno" },
    { value: "ISPETTIVO", label: "Ispezione / ASL" },
    { value: "AUDIT", label: "Audit di conformità" },
];

const GRAVITA = [
    { value: "MINORE", label: "Minore" },
    { value: "MAGGIORE", label: "Maggiore" },
    { value: "CRITICA", label: "Critica" },
];

const STATI_NC = [
    { value: "APERTA", label: "Aperta" },
    { value: "IN_CORSO", label: "In corso" },
    { value: "CHIUSA", label: "Chiusa" },
];

function apiErr(err, fallback) {
    if (!err) return fallback;
    if (typeof err === "string") return err;
    const m = err.message || err.messaggio || err.error?.message || err.error;
    if (typeof m === "string" && m.trim()) return m;
    if (m && typeof m === "object" && m.message) return m.message;
    return fallback;
}

function fmtDate(d) {
    if (!d) return "—";
    const s = String(d).substring(0, 10);
    const p = s.split("-");
    if (p.length !== 3) return s;
    return `${p[2]}/${p[1]}/${p[0]}`;
}

function gravitaBadge(g) {
    const map = { CRITICA: "danger", MAGGIORE: "warning", MINORE: "secondary" };
    return <Badge bg={map[g] || "secondary"} text={g === "MAGGIORE" ? "dark" : undefined}>{g || "—"}</Badge>;
}

function scadenzaBadge(nc) {
    if (nc.stato_azione === "CHIUSA") return <Badge bg="success">Chiusa</Badge>;
    const map = {
        SCADUTA: { bg: "danger", label: "Scaduta" },
        IN_SCADENZA: { bg: "warning", label: "In scadenza" },
        VALIDA: { bg: "success", label: "In regola" },
        SENZA_SCADENZA: { bg: "light", label: "Senza scadenza" },
    };
    const m = map[nc.stato_scadenza] || map.SENZA_SCADENZA;
    return (
        <span>
            <Badge bg={m.bg} text={m.bg === "light" || m.bg === "warning" ? "dark" : undefined} className={m.bg === "light" ? "border" : undefined}>{m.label}</Badge>
            {typeof nc.giorni_alla_scadenza === "number" && nc.stato_scadenza !== "SENZA_SCADENZA" && (
                <span className="text-muted small ms-1">
                    ({nc.giorni_alla_scadenza < 0 ? `${Math.abs(nc.giorni_alla_scadenza)} gg fa` : `tra ${nc.giorni_alla_scadenza} gg`})
                </span>
            )}
        </span>
    );
}

export default function SopralluoghiPanel({ idAzienda }) {
    const annoCorrente = new Date().getFullYear();
    const [anno, setAnno] = useState(annoCorrente);
    const [sopralluoghi, setSopralluoghi] = useState([]);
    const [nonConformita, setNonConformita] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [soloAperte, setSoloAperte] = useState(true);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState(null);
    const [showSop, setShowSop] = useState(false);
    const [showNc, setShowNc] = useState(false);
    const [editNc, setEditNc] = useState(null);
    const [formSop, setFormSop] = useState(EMPTY_SOP);
    const [formNc, setFormNc] = useState(EMPTY_NC);
    const [fileSop, setFileSop] = useState(null);
    const [fileNc, setFileNc] = useState(null);
    const [saving, setSaving] = useState(false);

    const parseJson = async (resp) => (resp && typeof resp.json === "function" ? await resp.json() : resp);

    const loadStats = useCallback(async () => {
        if (!idAzienda) { setStats(null); return; }
        try {
            setStats(await parseJson(await SafetySopralluoghiService.stats(idAzienda)));
        } catch { setStats(null); }
    }, [idAzienda]);

    const loadSopralluoghi = useCallback(async () => {
        if (!idAzienda) { setSopralluoghi([]); return; }
        setLoading(true);
        setError(null);
        try {
            const data = await parseJson(await SafetySopralluoghiService.byAzienda(idAzienda, anno));
            if (data && !Array.isArray(data) && (data.message || data.messaggio || data.error)) {
                throw new Error(apiErr(data, "Risposta API non valida."));
            }
            setSopralluoghi(Array.isArray(data) ? data : []);
        } catch (err) {
            const m = apiErr(err, "");
            setError(m.includes("NetworkError") || m.includes("Failed to fetch")
                ? "API non raggiungibile. Verifica deploy: SafetySopralluoghiController + Service, auth.json, SQL 2026-06-14."
                : (m || "Errore caricamento sopralluoghi. Controlla SQL (safety_sopralluoghi / safety_non_conformita) e deploy backend."));
        } finally {
            setLoading(false);
        }
    }, [idAzienda, anno]);

    const loadNc = useCallback(async () => {
        if (!idAzienda) { setNonConformita([]); return; }
        try {
            const data = await parseJson(await SafetySopralluoghiService.nonConformitaByAzienda(idAzienda, soloAperte));
            setNonConformita(Array.isArray(data) ? data : []);
        } catch { setNonConformita([]); }
    }, [idAzienda, soloAperte]);

    useEffect(() => { loadStats(); }, [loadStats]);
    useEffect(() => { loadSopralluoghi(); }, [loadSopralluoghi]);
    useEffect(() => { loadNc(); }, [loadNc]);

    const refreshAll = async () => {
        await loadStats();
        await loadSopralluoghi();
        await loadNc();
    };

    const openSopForm = () => {
        setFormSop({ ...EMPTY_SOP, data_sopralluogo: new Date().toISOString().substring(0, 10) });
        setFileSop(null);
        setShowSop(true);
        setError(null);
    };

    const openNcForm = (idSopralluogo = "") => {
        setEditNc(null);
        setFormNc({
            ...EMPTY_NC,
            id_sopralluogo: idSopralluogo ? String(idSopralluogo) : "",
            data_rilevo: new Date().toISOString().substring(0, 10),
        });
        setFileNc(null);
        setShowNc(true);
        setError(null);
    };

    const openEditNc = (nc) => {
        setEditNc(nc);
        setFormNc({
            id_sopralluogo: nc.id_sopralluogo ? String(nc.id_sopralluogo) : "",
            codice: nc.codice || "",
            gravita: nc.gravita || "MINORE",
            descrizione: nc.descrizione || "",
            riferimento_normativo: nc.riferimento_normativo || "",
            area_rilevazione: nc.area_rilevazione || "",
            responsabile: nc.responsabile || "",
            data_rilevo: nc.data_rilevo ? String(nc.data_rilevo).substring(0, 10) : "",
            data_scadenza_azione: nc.data_scadenza_azione ? String(nc.data_scadenza_azione).substring(0, 10) : "",
            stato_azione: nc.stato_azione || "APERTA",
            azione_correttiva: nc.azione_correttiva || "",
            note: nc.note || "",
        });
        setFileNc(null);
        setShowNc(true);
    };

    const submitSop = async () => {
        if (!formSop.data_sopralluogo) { setError("Indica la data del sopralluogo."); return; }
        setSaving(true);
        setError(null);
        try {
            const fd = new FormData();
            fd.append("id_azienda", idAzienda);
            fd.append("tipo", formSop.tipo);
            fd.append("data_sopralluogo", formSop.data_sopralluogo);
            if (formSop.luogo) fd.append("luogo", formSop.luogo);
            if (formSop.oggetto) fd.append("oggetto", formSop.oggetto);
            if (formSop.ispettore) fd.append("ispettore", formSop.ispettore);
            if (formSop.esito_sintesi) fd.append("esito_sintesi", formSop.esito_sintesi);
            if (formSop.note) fd.append("note", formSop.note);
            if (fileSop) fd.append("file", fileSop);
            await SafetySopralluoghiService.createSopralluogo(fd);
            setMsg("Sopralluogo registrato.");
            setShowSop(false);
            await refreshAll();
        } catch (err) {
            setError(err?.message || "Errore nel salvataggio.");
        } finally {
            setSaving(false);
        }
    };

    const submitNc = async () => {
        if (!formNc.descrizione?.trim()) { setError("Descrizione della non conformità obbligatoria."); return; }
        setSaving(true);
        setError(null);
        try {
            const fd = new FormData();
            fd.append("id_azienda", idAzienda);
            if (editNc) {
                fd.append("id_non_conformita", editNc.id_non_conformita);
                fd.append("stato_azione", formNc.stato_azione);
                fd.append("azione_correttiva", formNc.azione_correttiva || "");
                fd.append("responsabile", formNc.responsabile || "");
                if (formNc.data_scadenza_azione) fd.append("data_scadenza_azione", formNc.data_scadenza_azione);
                if (formNc.stato_azione === "CHIUSA") fd.append("data_chiusura", new Date().toISOString().substring(0, 10));
                if (fileNc) fd.append("file", fileNc);
                await SafetySopralluoghiService.updateNonConformita(fd);
                setMsg("Non conformità aggiornata.");
            } else {
                if (formNc.id_sopralluogo) fd.append("id_sopralluogo", formNc.id_sopralluogo);
                fd.append("gravita", formNc.gravita);
                fd.append("descrizione", formNc.descrizione);
                fd.append("stato_azione", formNc.stato_azione);
                if (formNc.codice) fd.append("codice", formNc.codice);
                if (formNc.riferimento_normativo) fd.append("riferimento_normativo", formNc.riferimento_normativo);
                if (formNc.area_rilevazione) fd.append("area_rilevazione", formNc.area_rilevazione);
                if (formNc.responsabile) fd.append("responsabile", formNc.responsabile);
                if (formNc.data_rilevo) fd.append("data_rilevo", formNc.data_rilevo);
                if (formNc.data_scadenza_azione) fd.append("data_scadenza_azione", formNc.data_scadenza_azione);
                if (formNc.azione_correttiva) fd.append("azione_correttiva", formNc.azione_correttiva);
                if (formNc.note) fd.append("note", formNc.note);
                if (fileNc) fd.append("file", fileNc);
                await SafetySopralluoghiService.createNonConformita(fd);
                setMsg("Non conformità registrata.");
            }
            setShowNc(false);
            setEditNc(null);
            await refreshAll();
        } catch (err) {
            setError(err?.message || "Errore nel salvataggio.");
        } finally {
            setSaving(false);
        }
    };

    if (!idAzienda) {
        return (
            <BsAlert variant="info" className="py-2 mb-0">
                Salva prima i dati dell'azienda per gestire sopralluoghi e non conformità.
            </BsAlert>
        );
    }

    return (
        <div>
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                <div>
                    <h6 className="mb-1 fw-semibold">
                        <i className="bi bi-search me-1 text-primary" />
                        Sopralluoghi e non conformità
                    </h6>
                    <div className="text-muted small">Audit interni, rilievi e azioni correttive con responsabili e scadenze</div>
                </div>
                <Form.Select size="sm" style={{ width: 100 }} value={anno} onChange={e => setAnno(Number(e.target.value))}>
                    {[annoCorrente - 2, annoCorrente - 1, annoCorrente, annoCorrente + 1].map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </Form.Select>
            </div>

            {stats && (
                <Row className="g-2 mb-3">
                    <Col xs={6} md={3}>
                        <Card className="border-0 bg-light h-100"><Card.Body className="py-2 px-3">
                            <div className="text-muted small">Sopralluoghi {anno}</div>
                            <div className="fw-bold fs-5">{stats.sopralluoghi_anno ?? 0}</div>
                        </Card.Body></Card>
                    </Col>
                    <Col xs={6} md={3}>
                        <Card className="border-0 bg-light h-100"><Card.Body className="py-2 px-3">
                            <div className="text-muted small">NC aperte</div>
                            <div className="fw-bold fs-5 text-danger">{stats.nc_aperte ?? 0}</div>
                        </Card.Body></Card>
                    </Col>
                    <Col xs={6} md={3}>
                        <Card className="border-0 bg-light h-100"><Card.Body className="py-2 px-3">
                            <div className="text-muted small">NC scadute</div>
                            <div className="fw-bold fs-5 text-warning">{stats.nc_scadute ?? 0}</div>
                        </Card.Body></Card>
                    </Col>
                    <Col xs={6} md={3}>
                        <Card className="border-0 bg-light h-100"><Card.Body className="py-2 px-3">
                            <div className="text-muted small">NC chiuse {anno}</div>
                            <div className="fw-bold fs-5 text-success">{stats.nc_chiuse_anno ?? 0}</div>
                        </Card.Body></Card>
                    </Col>
                </Row>
            )}

            {error && <BsAlert variant="danger" className="py-2 small" onClose={() => setError(null)} dismissible>{error}</BsAlert>}
            {msg && <BsAlert variant="success" className="py-2 small" onClose={() => setMsg(null)} dismissible>{msg}</BsAlert>}

            <Tab.Container defaultActiveKey="sopralluoghi">
                <Nav variant="tabs" className="mb-3 small">
                    <Nav.Item><Nav.Link eventKey="sopralluoghi">Sopralluoghi</Nav.Link></Nav.Item>
                    <Nav.Item><Nav.Link eventKey="nc">Non conformità</Nav.Link></Nav.Item>
                </Nav>
                <Tab.Content>
                    <Tab.Pane eventKey="sopralluoghi">
                        <div className="d-flex justify-content-end mb-2">
                            <Button size="sm" variant="primary" onClick={openSopForm}>
                                <i className="bi bi-plus-lg me-1" />Nuovo sopralluogo
                            </Button>
                        </div>
                        {showSop && (
                            <Card className="mb-3 border-primary">
                                <Card.Header className="py-2 bg-light small fw-semibold">Registra sopralluogo</Card.Header>
                                <Card.Body className="py-3">
                                    <Row className="g-2">
                                        <Col md={3}>
                                            <Form.Label className="small">Tipo</Form.Label>
                                            <Form.Select size="sm" value={formSop.tipo} onChange={e => setFormSop(f => ({ ...f, tipo: e.target.value }))}>
                                                {TIPI_SOP.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                            </Form.Select>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Label className="small">Data</Form.Label>
                                            <Form.Control size="sm" type="date" value={formSop.data_sopralluogo} onChange={e => setFormSop(f => ({ ...f, data_sopralluogo: e.target.value }))} />
                                        </Col>
                                        <Col md={3}>
                                            <Form.Label className="small">Luogo</Form.Label>
                                            <Form.Control size="sm" value={formSop.luogo} onChange={e => setFormSop(f => ({ ...f, luogo: e.target.value }))} />
                                        </Col>
                                        <Col md={3}>
                                            <Form.Label className="small">Ispettore / auditor</Form.Label>
                                            <Form.Control size="sm" value={formSop.ispettore} onChange={e => setFormSop(f => ({ ...f, ispettore: e.target.value }))} />
                                        </Col>
                                        <Col md={12}>
                                            <Form.Label className="small">Oggetto</Form.Label>
                                            <Form.Control size="sm" value={formSop.oggetto} onChange={e => setFormSop(f => ({ ...f, oggetto: e.target.value }))} placeholder="Es. Verifica DPI, uscite emergenza, armamento…" />
                                        </Col>
                                        <Col md={12}>
                                            <Form.Label className="small">Esito / sintesi</Form.Label>
                                            <Form.Control as="textarea" rows={2} size="sm" value={formSop.esito_sintesi} onChange={e => setFormSop(f => ({ ...f, esito_sintesi: e.target.value }))} />
                                        </Col>
                                        <Col md={12}>
                                            <Form.Label className="small">Allegato verbale / report</Form.Label>
                                            <Form.Control size="sm" type="file" accept=".pdf,.doc,.docx" onChange={e => setFileSop(e.target.files?.[0] || null)} />
                                        </Col>
                                    </Row>
                                    <div className="d-flex gap-2 mt-2">
                                        <Button size="sm" variant="primary" disabled={saving} onClick={submitSop}>
                                            {saving ? <Spinner animation="border" size="sm" /> : "Salva"}
                                        </Button>
                                        <Button size="sm" variant="outline-secondary" onClick={() => setShowSop(false)}>Annulla</Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        )}
                        {loading ? <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                        : sopralluoghi.length === 0 ? (
                            <BsAlert variant="light" className="py-2 small border text-muted">Nessun sopralluogo per {anno}.</BsAlert>
                        ) : (
                            <Table responsive hover size="sm" className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>Data</th><th>Tipo</th><th>Oggetto</th><th>Ispettore</th><th>NC</th><th>Allegato</th><th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {sopralluoghi.map(s => (
                                        <tr key={s.id_sopralluogo}>
                                            <td>{fmtDate(s.data_sopralluogo)}</td>
                                            <td><Badge bg="light" text="dark" className="border">{s.tipo}</Badge></td>
                                            <td className="small">{s.oggetto || "—"}</td>
                                            <td className="small text-muted">{s.ispettore || "—"}</td>
                                            <td>
                                                {s.nc_aperte > 0
                                                    ? <Badge bg="danger">{s.nc_aperte} aperte</Badge>
                                                    : <Badge bg="success">{s.nc_totali || 0} tot.</Badge>}
                                            </td>
                                            <td>{s.allegato_presente ? <Badge bg="success">Sì</Badge> : "—"}</td>
                                            <td className="text-end text-nowrap">
                                                <Button size="sm" variant="outline-secondary" className="me-1" title="Aggiungi NC" onClick={() => openNcForm(s.id_sopralluogo)}>
                                                    <i className="bi bi-exclamation-triangle" />
                                                </Button>
                                                {s.path_file && (
                                                    <Button size="sm" variant="outline-primary" className="me-1" onClick={() => SafetySopralluoghiService.download(s.id_sopralluogo, `sopralluogo_${s.id_sopralluogo}.pdf`)}>
                                                        <i className="bi bi-download" />
                                                    </Button>
                                                )}
                                                <Button size="sm" variant="outline-danger" onClick={async () => {
                                                    if (!window.confirm("Eliminare il sopralluogo?")) return;
                                                    await SafetySopralluoghiService.deleteSopralluogo(s.id_sopralluogo);
                                                    await refreshAll();
                                                }}><i className="bi bi-trash" /></Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Tab.Pane>

                    <Tab.Pane eventKey="nc">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                            <Form.Check type="switch" id="solo-aperte" label="Solo aperte" checked={soloAperte} onChange={e => setSoloAperte(e.target.checked)} className="small" />
                            <Button size="sm" variant="primary" onClick={() => openNcForm()}>
                                <i className="bi bi-plus-lg me-1" />Nuova non conformità
                            </Button>
                        </div>
                        {showNc && (
                            <Card className="mb-3 border-warning">
                                <Card.Header className="py-2 bg-light small fw-semibold">
                                    {editNc ? "Aggiorna azione correttiva" : "Registra non conformità"}
                                </Card.Header>
                                <Card.Body className="py-3">
                                    <Row className="g-2">
                                        {!editNc && (
                                            <>
                                                <Col md={4}>
                                                    <Form.Label className="small">Collega a sopralluogo</Form.Label>
                                                    <Form.Select size="sm" value={formNc.id_sopralluogo} onChange={e => setFormNc(f => ({ ...f, id_sopralluogo: e.target.value }))}>
                                                        <option value="">— Standalone —</option>
                                                        {sopralluoghi.map(s => (
                                                            <option key={s.id_sopralluogo} value={s.id_sopralluogo}>
                                                                {fmtDate(s.data_sopralluogo)} — {s.oggetto || s.tipo}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Col>
                                                <Col md={2}>
                                                    <Form.Label className="small">Codice</Form.Label>
                                                    <Form.Control size="sm" value={formNc.codice} onChange={e => setFormNc(f => ({ ...f, codice: e.target.value }))} placeholder="NC-001" />
                                                </Col>
                                                <Col md={2}>
                                                    <Form.Label className="small">Gravità</Form.Label>
                                                    <Form.Select size="sm" value={formNc.gravita} onChange={e => setFormNc(f => ({ ...f, gravita: e.target.value }))}>
                                                        {GRAVITA.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                                                    </Form.Select>
                                                </Col>
                                                <Col md={4}>
                                                    <Form.Label className="small">Area / reparto</Form.Label>
                                                    <Form.Control size="sm" value={formNc.area_rilevazione} onChange={e => setFormNc(f => ({ ...f, area_rilevazione: e.target.value }))} />
                                                </Col>
                                                <Col md={12}>
                                                    <Form.Label className="small">Descrizione rilievo</Form.Label>
                                                    <Form.Control as="textarea" rows={2} size="sm" value={formNc.descrizione} onChange={e => setFormNc(f => ({ ...f, descrizione: e.target.value }))} />
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Label className="small">Riferimento normativo</Form.Label>
                                                    <Form.Control size="sm" value={formNc.riferimento_normativo} onChange={e => setFormNc(f => ({ ...f, riferimento_normativo: e.target.value }))} />
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Label className="small">Data rilevazione</Form.Label>
                                                    <Form.Control size="sm" type="date" value={formNc.data_rilevo} onChange={e => setFormNc(f => ({ ...f, data_rilevo: e.target.value }))} />
                                                </Col>
                                            </>
                                        )}
                                        <Col md={4}>
                                            <Form.Label className="small">Responsabile azione</Form.Label>
                                            <Form.Control size="sm" value={formNc.responsabile} onChange={e => setFormNc(f => ({ ...f, responsabile: e.target.value }))} />
                                        </Col>
                                        <Col md={3}>
                                            <Form.Label className="small">Scadenza azione</Form.Label>
                                            <Form.Control size="sm" type="date" value={formNc.data_scadenza_azione} onChange={e => setFormNc(f => ({ ...f, data_scadenza_azione: e.target.value }))} />
                                        </Col>
                                        <Col md={3}>
                                            <Form.Label className="small">Stato</Form.Label>
                                            <Form.Select size="sm" value={formNc.stato_azione} onChange={e => setFormNc(f => ({ ...f, stato_azione: e.target.value }))}>
                                                {STATI_NC.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                            </Form.Select>
                                        </Col>
                                        <Col md={12}>
                                            <Form.Label className="small">Azione correttiva</Form.Label>
                                            <Form.Control as="textarea" rows={2} size="sm" value={formNc.azione_correttiva} onChange={e => setFormNc(f => ({ ...f, azione_correttiva: e.target.value }))} />
                                        </Col>
                                        <Col md={12}>
                                            <Form.Label className="small">Allegato evidenza / chiusura</Form.Label>
                                            <Form.Control size="sm" type="file" accept=".pdf,.doc,.docx,.jpg,.png" onChange={e => setFileNc(e.target.files?.[0] || null)} />
                                        </Col>
                                    </Row>
                                    <div className="d-flex gap-2 mt-2">
                                        <Button size="sm" variant="primary" disabled={saving} onClick={submitNc}>
                                            {saving ? <Spinner animation="border" size="sm" /> : "Salva"}
                                        </Button>
                                        <Button size="sm" variant="outline-secondary" onClick={() => { setShowNc(false); setEditNc(null); }}>Annulla</Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        )}
                        {nonConformita.length === 0 ? (
                            <BsAlert variant="light" className="py-2 small border text-muted">Nessuna non conformità{soloAperte ? " aperta" : ""}.</BsAlert>
                        ) : (
                            <Table responsive hover size="sm" className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>Codice</th><th>Gravità</th><th>Descrizione</th><th>Responsabile</th><th>Scadenza</th><th>Stato</th><th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {nonConformita.map(nc => (
                                        <tr key={nc.id_non_conformita}>
                                            <td className="small">{nc.codice || `#${nc.id_non_conformita}`}</td>
                                            <td>{gravitaBadge(nc.gravita)}</td>
                                            <td className="small" style={{ maxWidth: 220 }}>
                                                <div className="text-truncate" title={nc.descrizione}>{nc.descrizione}</div>
                                                {nc.sop_data && <div className="text-muted" style={{ fontSize: "0.75rem" }}>Sop. {fmtDate(nc.sop_data)}</div>}
                                            </td>
                                            <td className="small">{nc.responsabile || "—"}</td>
                                            <td>{fmtDate(nc.data_scadenza_azione)}</td>
                                            <td>{scadenzaBadge(nc)}</td>
                                            <td className="text-end text-nowrap">
                                                <Button size="sm" variant="outline-secondary" className="me-1" title="Gestisci" onClick={() => openEditNc(nc)}>
                                                    <i className="bi bi-pencil" />
                                                </Button>
                                                {nc.path_file && (
                                                    <Button size="sm" variant="outline-primary" className="me-1" onClick={() => SafetySopralluoghiService.downloadNc(nc.id_non_conformita, `nc_${nc.id_non_conformita}.pdf`)}>
                                                        <i className="bi bi-download" />
                                                    </Button>
                                                )}
                                                <Button size="sm" variant="outline-danger" onClick={async () => {
                                                    if (!window.confirm("Eliminare la non conformità?")) return;
                                                    await SafetySopralluoghiService.deleteNonConformita(nc.id_non_conformita);
                                                    await refreshAll();
                                                }}><i className="bi bi-trash" /></Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        </div>
    );
}
